/**
 * Numbered Steps — vertikale, nummerierte Schritt-für-Schritt-Liste.
 *
 * Die „Erklär-Szene" schlechthin: eine vertikale Spur mit nummerierten
 * Disc-Badges (01, 02, 03 …), einer durchgezogenen Verbindungslinie, die
 * sich von Schritt zu Schritt nach unten zeichnet, sowie Titel + Caption je
 * Schritt. Perfekt für „So funktioniert's", Onboarding, Prozesse.
 *
 * Motion:
 *   - Optionaler Header (Eyebrow + Headline) steigt ein.
 *   - Pro Schritt: die Linie wächst nach unten → das Badge poppt (Spring) →
 *     Titel + Caption gleiten von links ein. Sauberer, lesbarer Stagger.
 *
 * Items-Format:  "Titel | Caption | iconKey"
 *   z. B. "Anbieter finden | In Sekunden in deiner Nähe | search"
 * (iconKey optional — ohne Key wird die Schrittnummer angezeigt.)
 */
import type { SceneBuilder } from "../../reel/scenes.js";
import { brandBackdrop } from "../../primitives/brandBackdrop.js";
import {
  accentFor, clipOutroFade, combineSceneFragments, escapeHtml, gsapSetLine,
  makeTl, typeSize, weights, withAlpha, colors as themeColors,
} from "../../reel/sceneShared.js";
import { lucideIcon, splitItem } from "../visual/_mockHelpers.js";

export const numberedSteps: SceneBuilder = ({ ctx, clipPrefix, inputs, clipDurationSec, clipStartSec }) => {
  const accent = accentFor(inputs);
  const violet = themeColors.secondary;
  const pink = themeColors.accent;
  const palette = [accent, violet, pink, accent, violet];
  const isVertical = ctx.format === "9:16";

  const eyebrow = inputs.extras?.eyebrow != null ? String(inputs.extras.eyebrow) : "";
  const headline = inputs.text ?? "";

  const items = (inputs.items?.length
    ? inputs.items
    : [
        "Anbieter finden | In Sekunden in deiner Nähe | search",
        "Direkt anfragen | Ohne Umwege, ohne Wartezeit | chat",
        "Sicher bezahlen | Stripe & Käuferschutz | lock",
        "Fertig | Lokal unterstützt, sofort erledigt | check",
      ]
  ).slice(0, isVertical ? 5 : 4);

  const lg = typeSize("lg", ctx.format);
  const md = typeSize("md", ctx.format);
  const sm = typeSize("sm", ctx.format);

  const wrapId = `${clipPrefix}-wrap`;
  const eyeId = `${clipPrefix}-eye`;
  const headId = `${clipPrefix}-head`;

  const parsed = items.map((raw, i) => {
    const [title, caption, iconRaw] = splitItem(raw);
    return {
      title: (title ?? "").trim(),
      caption: (caption ?? "").trim(),
      icon: (iconRaw ?? "").trim(),
      color: palette[i % palette.length] ?? accent,
    };
  });

  const badge = isVertical ? 92 : 84;
  const titlePx = isVertical ? md * 0.74 : md * 0.66;
  const capPx = isVertical ? sm * 0.66 : sm * 0.6;
  const numPx = Math.round(badge * 0.4);

  const badgeIds = parsed.map((_, i) => `${clipPrefix}-bdg-${i}`);
  const lineIds = parsed.map((_, i) => `${clipPrefix}-line-${i}`);
  const titleIds = parsed.map((_, i) => `${clipPrefix}-tt-${i}`);
  const capIds = parsed.map((_, i) => `${clipPrefix}-cap-${i}`);

  const t = makeTl(clipStartSec);
  const backdrop = brandBackdrop(ctx, `${clipPrefix}-bg`, { variant: "allyver", accent });

  function renderStep(i: number, it: (typeof parsed)[number]): string {
    const color = it.color;
    const isLast = i === parsed.length - 1;
    const num = String(i + 1).padStart(2, "0");
    const inner = it.icon
      ? lucideIcon(it.icon, Math.round(badge * 0.46), themeColors.bg, 2.2)
      : `<span style="font-family:'Outfit',sans-serif;font-weight:${weights.black};font-size:${numPx}px;color:${themeColors.bg};letter-spacing:-1px;">${num}</span>`;
    return `<div style="position:relative;display:flex;align-items:flex-start;gap:${isVertical ? 26 : 30}px;">
      <div style="position:relative;display:flex;flex-direction:column;align-items:center;flex-shrink:0;">
        <div id="${badgeIds[i]}" style="width:${badge}px;height:${badge}px;border-radius:9999px;display:flex;align-items:center;justify-content:center;background:radial-gradient(circle at 32% 28%, ${color} 0%, ${withAlpha(color, 0.82)} 100%);box-shadow:0 0 30px ${withAlpha(color, 0.5)}, inset 0 1px 0 ${withAlpha("#ffffff", 0.4)};will-change:transform,opacity;">${inner}</div>
        ${isLast ? "" : `<div id="${lineIds[i]}" style="width:3px;flex:1;min-height:${isVertical ? 52 : 44}px;margin-top:6px;background:linear-gradient(180deg, ${withAlpha(color, 0.8)} 0%, ${withAlpha(palette[(i + 1) % palette.length] ?? accent, 0.6)} 100%);transform-origin:50% 0%;border-radius:2px;"></div>`}
      </div>
      <div style="display:flex;flex-direction:column;gap:6px;padding-top:${Math.round(badge * 0.12)}px;padding-bottom:${isVertical ? 18 : 14}px;">
        <div id="${titleIds[i]}" style="font-family:'Outfit',sans-serif;font-weight:${weights.black};font-size:${titlePx}px;line-height:1.04;letter-spacing:-1px;color:${themeColors.fg};text-wrap:balance;">${escapeHtml(it.title)}</div>
        ${it.caption ? `<div id="${capIds[i]}" style="font-family:'Inter',sans-serif;font-weight:${weights.semibold};font-size:${capPx}px;letter-spacing:0.1px;line-height:1.3;color:${withAlpha(themeColors.fg, 0.72)};text-wrap:balance;">${escapeHtml(it.caption)}</div>` : ""}
      </div>
    </div>`;
  }

  const stepsHtml = parsed.map((it, i) => renderStep(i, it)).join("");
  const eyePx = sm * 0.6;
  const headPx = isVertical ? lg * 0.84 : lg * 0.76;
  const hasHeader = Boolean(headline || eyebrow);

  const html = `<div id="${wrapId}" class="clip" data-start="${clipStartSec.toFixed(4)}" data-duration="${clipDurationSec.toFixed(4)}" style="position:absolute;inset:0;display:flex;flex-direction:column;align-items:stretch;justify-content:center;gap:${isVertical ? 34 : 30}px;padding:${isVertical ? "120px 96px" : "110px 150px"};overflow:hidden;">
    ${hasHeader ? `<div style="display:flex;flex-direction:column;gap:12px;max-width:${isVertical ? 940 : 1300}px;">
      ${eyebrow ? `<div id="${eyeId}" style="font-family:'Outfit',sans-serif;font-weight:${weights.black};font-size:${eyePx}px;letter-spacing:6px;text-transform:uppercase;color:${accent};display:inline-flex;align-items:center;gap:14px;"><span style="width:34px;height:2px;background:${accent};"></span><span>${escapeHtml(eyebrow)}</span></div>` : ""}
      ${headline ? `<div id="${headId}" style="font-family:'Outfit',sans-serif;font-weight:${weights.black};font-size:${headPx}px;line-height:1.04;letter-spacing:-1.8px;color:${themeColors.fg};text-wrap:balance;">${escapeHtml(headline)}</div>` : ""}
    </div>` : ""}
    <div style="display:flex;flex-direction:column;">${stepsHtml}</div>
  </div>`;

  const init = [
    eyebrow ? gsapSetLine(`#${eyeId}`, { opacity: 0, y: -8 }) : "",
    headline ? gsapSetLine(`#${headId}`, { opacity: 0, y: 22 }) : "",
    ...badgeIds.map((id) => gsapSetLine(`#${id}`, { scale: 0, transformOrigin: "50% 50%" })),
    ...lineIds.slice(0, -1).map((id) => gsapSetLine(`#${id}`, { scaleY: 0 })),
    ...titleIds.map((id) => gsapSetLine(`#${id}`, { opacity: 0, x: -22 })),
    ...capIds.map((id, i) => (parsed[i].caption ? gsapSetLine(`#${id}`, { opacity: 0, x: -18 }) : "")),
  ].filter(Boolean).join("\n");

  const tweens: string[] = [];
  if (eyebrow) tweens.push(t.to(`#${eyeId}`, { opacity: 1, y: 0, duration: 0.4, ease: "power2.out" }, 0.1));
  if (headline) tweens.push(t.to(`#${headId}`, { opacity: 1, y: 0, duration: 0.55, ease: "power3.out" }, 0.2));

  const start = hasHeader ? 0.55 : 0.25;
  const revealWindow = Math.min(2.4, Math.max(1.0, clipDurationSec * 0.6));
  const step = revealWindow / Math.max(parsed.length, 1);

  parsed.forEach((it, i) => {
    const at = start + i * step;
    // Vorherige Verbindungslinie zeichnet sich nach unten, dann poppt das Badge.
    if (i > 0 && lineIds[i - 1]) {
      tweens.push(t.to(`#${lineIds[i - 1]}`, { scaleY: 1, duration: step * 0.6, ease: "power2.inOut" }, at - step * 0.45));
    }
    tweens.push(t.to(`#${badgeIds[i]}`, { scale: 1, duration: 0.5, ease: "back.out(2.1)" }, at));
    tweens.push(t.to(`#${titleIds[i]}`, { opacity: 1, x: 0, duration: 0.42, ease: "power2.out" }, at + 0.08));
    if (it.caption) tweens.push(t.to(`#${capIds[i]}`, { opacity: 1, x: 0, duration: 0.4, ease: "power2.out" }, at + 0.15));
  });

  tweens.push(clipOutroFade(clipPrefix, clipDurationSec, clipStartSec));

  return combineSceneFragments([backdrop, { html, init, tweens }]);
};
